/**
 * Owner: Haselton Baker Risk Group, LLC
 * Product: SP3 - SP3-Ui
 * Developer: Mike McGlone <mike@hbrisk.com>
 * Collaborators: Patrick Barnum <patrick@hbrisk.com>
 * Date: 11/21/17
 * Copyright All Rights Reserved
 */
// @flow
import React, { Fragment } from 'react';
import classNames from 'classnames';
import { isEmpty } from 'lodash/fp';
import numeral from 'numeral';
import {
  withScriptjs,
  withGoogleMap,
  GoogleMap,
} from 'react-google-maps';
import { compose, withProps } from 'recompose';
import config, { GOOGLE_MAPS_API_KEY } from 'support/config';
import googleMapURL from './googleMapURL';
import mapStyles from './mapStyles.json';
import uuidv4 from 'uuid/v4';
import ToggleableMarkerWithLabel from './ToggleableMarkerWithLabel';
import pickColor from 'support/pickColor';
import getOccupancyDescription from 'support/getOccupancyDescription';
import getBuildingTypeDescription from 'support/getBuildingTypeDescription';
import { performanceMetricsById, REPAIR_COSTS_ID } from 'constants/performanceMetrics';

const getCenterPoint = (models) => {
  const res = models.reduce((acc, curr) => ({
    latMin: acc.latMin ? Math.min(acc.latMin, curr.lat) : curr.lat,
    latMax: acc.latMax ? Math.max(acc.latMax, curr.lat) : curr.lat,
    lngMin: acc.lngMin ? Math.min(acc.lngMin, curr.lng) : curr.lng,
    lngMax: acc.lngMax ? Math.max(acc.lngMax, curr.lng) : curr.lng,
  }), {});

  const {
    latMin, latMax, lngMin, lngMax,
  } = res;

  return {
    lat: (latMin + latMax) / 2,
    lng: (lngMin + lngMax) / 2,
  };
};

const EnhancedGoogleMap = withGoogleMap(({
  models, performanceMetric, markerProps, ...rest
}) => {
  declare var google: Object;
  const center = getCenterPoint(models);
  const markers = models.map((model) => {
    const {
      lat,
      lng,
      buildingValue,
      yearOfConstruction,
      numberOfStories,
      buildingType,
      buildingSquareFootage,
      occupancy,
      result: { value: resultValue },
    } = model;
    const value = performanceMetric === REPAIR_COSTS_ID && resultValue !== null
      ? resultValue / buildingValue
      : resultValue;

    const { name, format } = performanceMetricsById[performanceMetric];

    return (
      <ToggleableMarkerWithLabel
        key={uuidv4()}
        /* eslint-disable no-undef */
        google={google}
        /* eslint-enable no-undef */
        markerColor={pickColor(performanceMetric, value)}
        markerProps={markerProps}
        position={{ lat, lng }}
        opacity="1"
        messageComp={(
          <Fragment>
            <div>Building Type: {getBuildingTypeDescription(buildingType)}</div>
            <div>Year of Construction: {yearOfConstruction}</div>
            <div>Number of Stories: {numberOfStories}</div>
            <div>Occupancy: {getOccupancyDescription(occupancy)}</div>
            {buildingSquareFootage && <div>Square Footage: {`${numeral(buildingSquareFootage).format('0,0')}`} sq. ft</div>}
            <div>Building Value: {`${numeral(buildingValue).format('$0,0')}`}</div>
            {value !== null && (
              <div>
                {name}: {format(value)}
              </div>
            )}
          </Fragment>
        )}
      />
    );
  });

  return (
    <GoogleMap
      {...(isEmpty(models) ? {} : { center })}
      defaultOptions={
        {
          styles: mapStyles,
        }
      }
      {...rest}
    >
      { markers }
    </GoogleMap>
  );
});

type Props = {
  models: Array<Object>,
  performanceMetric: string,
  defaultZoom?: number,
  mapHeight?: number | string,
  markerProps?: Object,
  wrapperClassName?: string,
  wrapperHeight: number | string,
}

const Map = ({
  models,
  performanceMetric,
  wrapperHeight,
  wrapperClassName,
  mapHeight,
  markerProps,
  ...rest
}: Props) => (
  <EnhancedGoogleMap
    {...rest}
    containerElement={
      <div className={classNames(wrapperClassName)} style={{ height: wrapperHeight }} />
    }
    mapElement={
      <div style={{ height: mapHeight }} />
    }
    markerProps={markerProps}
    models={models}
    performanceMetric={performanceMetric}
  />
);

Map.defaultProps = {
  defaultZoom: 12,
  mapHeight: '100%',
  markerProps: {},
  wrapperClassName: '',
};

export default compose(
  withProps({
    googleMapURL: googleMapURL(config(GOOGLE_MAPS_API_KEY)),
    loadingElement: <div />,
  }),
  withScriptjs
)(Map);



// WEBPACK FOOTER //
// ./js/components/pages/Inventory/InventoryMap/Map/Map.jsx